import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { insuranceApiMethods } from '../utils/api';
import './MonitoringDashboard.css';

function MonitoringDashboard({ uploadedFiles }) {
  const location = useLocation();
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    extracting: 0,
    autoApproving: 0,
    complete: 0
  });
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updateCount, setUpdateCount] = useState(0);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [modalView, setModalView] = useState('both');
  const [openAccordions, setOpenAccordions] = useState({});
  const [filters, setFilters] = useState({
    company: 'all',
    status: 'all',
    searchTerm: '',
    dateFrom: null,
    dateTo: null
  });
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc' for timestamp sorting
  const [sortField, setSortField] = useState('timestamp'); // 'timestamp' or 'rules_passed'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [trendPeriod, setTrendPeriod] = useState(7); // 7, 30, 90 days
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [showExportMenu, setShowExportMenu] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      // Call API Gateway endpoint using authenticated axios client
      const response = await insuranceApiMethods.getDocuments();
      const data = response.data;
      setDocuments(data.items || []);
      calculateStats(data.items || []);
      setLastUpdate(new Date());
      setUpdateCount(prev => prev + 1);
    } catch (error) {
      console.error('Error fetching documents:', error);
      if (error.response?.status === 401) {
        console.error('Authentication failed - redirecting to login');
      }
    }
  }, []);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [documents, filters, sortOrder, sortField]);

  const applyFilters = () => {
    let filtered = [...documents];

    // Filter by company
    if (filters.company !== 'all') {
      filtered = filtered.filter(doc => doc.company === filters.company);
    }

    // Filter by approval status
    if (filters.status !== 'all') {
      filtered = filtered.filter(doc => {
        const decision = getApprovalDecision(doc);
        if (!decision) return false;
        
        const lowerDecision = decision.toLowerCase();
        if (filters.status === 'approved') {
          // Check if approved (not "not approved")
          return !lowerDecision.includes('not approved') && 
                 !lowerDecision.includes('reject') && 
                 lowerDecision.includes('approved');
        } else if (filters.status === 'not_approved') {
          // Check if not approved or rejected
          return lowerDecision.includes('not approved') || 
                 lowerDecision.includes('reject') ||
                 lowerDecision.includes('denied');
        }
        return true;
      });
    }

    // Filter by search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(doc => 
        (doc.doc_id && doc.doc_id.toLowerCase().includes(searchLower)) ||
        (doc.loan_guid && doc.loan_guid.toLowerCase().includes(searchLower)) ||
        (doc.company && doc.company.toLowerCase().includes(searchLower))
      );
    }

    // Sort: In-progress first, then by selected field
    filtered.sort((a, b) => {
      const progressA = getProgressPercentage(a);
      const progressB = getProgressPercentage(b);
      
      // In-progress (< 100%) comes before complete (100%)
      if (progressA < 100 && progressB === 100) return -1;
      if (progressA === 100 && progressB < 100) return 1;
      
      // Within same status, sort by selected field
      if (sortField === 'rules_passed') {
        const rulesA = getRulesPassedCount(a);
        const rulesB = getRulesPassedCount(b);
        return sortOrder === 'desc' ? rulesB - rulesA : rulesA - rulesB;
      } else {
        const timeA = a.initial_timestamp || 0;
        const timeB = b.initial_timestamp || 0;
        return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
      }
    });

    setFilteredDocs(filtered);
  };
  
  const getRulesPassedCount = (doc) => {
    try {
      const autoApprovalResults = parseAutoApprovalResults(doc.auto_approval_results);
      if (autoApprovalResults && autoApprovalResults.num_pass !== undefined) {
        return autoApprovalResults.num_pass;
      }
      return -1; // Return -1 for docs without results (will sort to bottom)
    } catch (error) {
      return -1;
    }
  };
  
  const getRulesDisplay = (doc) => {
    try {
      const autoApprovalResults = parseAutoApprovalResults(doc.auto_approval_results);
      if (autoApprovalResults && autoApprovalResults.num_pass !== undefined && autoApprovalResults.query_count !== undefined) {
        return `${autoApprovalResults.num_pass}/${autoApprovalResults.query_count}`;
      }
      return '--';
    } catch (error) {
      return '--';
    }
  };
  
  const handleSortChange = (field) => {
    if (sortField === field) {
      // Toggle order if clicking same field
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      // New field, default to desc
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const calculateStats = (docs) => {
    const stats = {
      total: docs.length,
      pending: 0,
      extracting: 0,
      autoApproving: 0,
      complete: 0
    };

    docs.forEach(doc => {
      const extractionStatus = doc.extraction_status || '';
      const autoApprovalStatus = doc.auto_approval_status || '';

      if (extractionStatus.includes('pending')) {
        stats.pending++;
      } else if (extractionStatus.includes('complete') && autoApprovalStatus.includes('pending')) {
        stats.extracting++;
      } else if (autoApprovalStatus.includes('in progress')) {
        stats.autoApproving++;
      } else if (autoApprovalStatus.includes('complete')) {
        stats.complete++;
      }
    });

    setStats(stats);
  };

  const startMonitoring = () => {
    setIsMonitoring(true);
  };

  const stopMonitoring = () => {
    setIsMonitoring(false);
  };

  const handleRefresh = () => {
    fetchDocuments();
  };

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    let interval;
    if (isMonitoring) {
      interval = setInterval(fetchDocuments, 5000); // Poll every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isMonitoring, fetchDocuments]);

  // Auto-open document details if navigated from dashboard
  useEffect(() => {
    if (location.state?.openDocId && documents.length > 0) {
      const docToOpen = documents.find(doc => doc.doc_id === location.state.openDocId);
      if (docToOpen) {
        viewDetails(docToOpen, 'both');
      }
    }
  }, [location.state, documents]);

  const getStatusBadgeClass = (extractionStatus, autoApprovalStatus) => {
    if (autoApprovalStatus && autoApprovalStatus.includes('complete')) {
      return 'status-complete';
    } else if (autoApprovalStatus && autoApprovalStatus.includes('in progress')) {
      return 'status-in-progress';
    } else if (extractionStatus && extractionStatus.includes('complete')) {
      return 'status-in-progress';
    } else if (extractionStatus && extractionStatus.includes('pending')) {
      return 'status-pending';
    }
    return 'status-pending';
  };

  const getStatusText = (extractionStatus, autoApprovalStatus) => {
    if (autoApprovalStatus && autoApprovalStatus.includes('complete')) {
      return 'Complete';
    } else if (autoApprovalStatus && autoApprovalStatus.includes('in progress')) {
      return 'In Progress';
    } else if (extractionStatus && extractionStatus.includes('complete')) {
      return 'In Progress';
    } else if (extractionStatus && extractionStatus.includes('pending')) {
      return 'Pending';
    }
    return 'Pending';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '--';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  const getProgressPercentage = (doc) => {
    const extractionStatus = doc.extraction_status || '';
    const autoApprovalStatus = doc.auto_approval_status || '';

    if (autoApprovalStatus.includes('complete')) return 100;
    if (autoApprovalStatus.includes('in progress')) return 75;
    if (extractionStatus.includes('complete')) return 50;
    if (extractionStatus.includes('pending')) return 25;
    return 0;
  };

  const parseExtractionValues = (extractionValues) => {
    try {
      if (typeof extractionValues === 'string') {
        return JSON.parse(extractionValues);
      }
      return extractionValues;
    } catch (error) {
      return null;
    }
  };

  const parseAutoApprovalResults = (results) => {
    try {
      let parsed = results;
      if (typeof results === 'string') {
        parsed = JSON.parse(results);
      }
      
      // Convert new format to array format if needed
      if (parsed && !Array.isArray(parsed.results)) {
        const criteriaArray = [];
        let passCount = 0;
        let totalCount = 0;
        
        Object.entries(parsed).forEach(([key, value]) => {
          // Skip final_decision and other non-criteria fields
          if (key === 'final_decision') return;
          
          if (value && typeof value === 'object' && 'criteria_met' in value) {
            totalCount++;
            const passed = value.criteria_met === true;
            if (passed) passCount++;
            
            criteriaArray.push({
              query: key,
              pass: passed,
              explanation: value.explanation || 'No explanation provided'
            });
          }
        });
        
        return {
          results: criteriaArray,
          final_decision: parsed.final_decision || 'Unknown',
          num_pass: passCount,
          query_count: totalCount
        };
      }
      
      return parsed;
    } catch (error) {
      console.error('Error parsing auto approval results:', error);
      return null;
    }
  };

  const [isDeletingSelected, setIsDeletingSelected] = useState(null);

  const toggleSelectDoc = (docId) => {
    const next = new Set(selectedDocs);
    next.has(docId) ? next.delete(docId) : next.add(docId);
    setSelectedDocs(next);
  };

  const toggleSelectAll = () => {
    if (selectedDocs.size === currentDocs.length) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(currentDocs.map(d => d.doc_id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDocs.size === 0) return;
    if (!window.confirm(`Delete ${selectedDocs.size} record(s)? This cannot be undone.`)) return;
    setIsDeletingSelected(true);
    const ids = Array.from(selectedDocs);
    const results = await Promise.allSettled(ids.map(id => insuranceApiMethods.deleteDocument(id)));
    const deleted = ids.filter((_, i) => results[i].status === 'fulfilled');
    setDocuments(prev => prev.filter(d => !deleted.includes(d.doc_id)));
    setSelectedDocs(new Set());
    setIsDeletingSelected(false);
  };

  const viewDetails = (doc, view = 'both') => {
    setSelectedDoc(doc);
    setModalView(view);
    
    // Initialize accordion states based on the document
    const autoApprovalResults = parseAutoApprovalResults(doc.auto_approval_results);
    const extractionValues = parseExtractionValues(doc.extraction_values);
    const initialStates = {};
    
    // Initialize criteria accordions
    if (autoApprovalResults && autoApprovalResults.results) {
      const isApproved = autoApprovalResults.final_decision?.toLowerCase().includes('approved');
      autoApprovalResults.results.forEach((result, idx) => {
        const shouldExpand = isApproved ? result.pass : !result.pass;
        initialStates[`criteria-${idx}`] = shouldExpand;
      });
    }
    
    // Initialize data accordions
    if (extractionValues) {
      const categories = categorizeData(extractionValues, doc.auto_approval_data);
      Object.keys(categories).forEach((categoryName, idx) => {
        initialStates[`data-${idx}`] = false; // Start collapsed
      });
    }
    
    setOpenAccordions(initialStates);
  };

  const toggleAccordion = (accordionId) => {
    setOpenAccordions(prev => ({
      ...prev,
      [accordionId]: !prev[accordionId]
    }));
  };

  const expandAllAccordions = (prefix) => {
    const newStates = {};
    
    // Get all potential accordion IDs
    if (selectedDoc) {
      if (prefix === 'criteria') {
        const autoApprovalResults = parseAutoApprovalResults(selectedDoc.auto_approval_results);
        if (autoApprovalResults && autoApprovalResults.results) {
          autoApprovalResults.results.forEach((result, idx) => {
            newStates[`criteria-${idx}`] = true;
          });
        }
      } else if (prefix === 'data') {
        const extractionValues = parseExtractionValues(selectedDoc.extraction_values);
        if (extractionValues) {
          const categories = categorizeData(extractionValues, selectedDoc.auto_approval_data);
          Object.keys(categories).forEach((categoryName, idx) => {
            if (Object.keys(categories[categoryName]).length > 0) {
              newStates[`data-${idx}`] = true;
            }
          });
        }
      }
    }
    
    setOpenAccordions(prev => ({ ...prev, ...newStates }));
  };

  const collapseAllAccordions = (prefix) => {
    const newStates = {};
    
    // Get all potential accordion IDs
    if (selectedDoc) {
      if (prefix === 'criteria') {
        const autoApprovalResults = parseAutoApprovalResults(selectedDoc.auto_approval_results);
        if (autoApprovalResults && autoApprovalResults.results) {
          autoApprovalResults.results.forEach((result, idx) => {
            newStates[`criteria-${idx}`] = false;
          });
        }
      } else if (prefix === 'data') {
        const extractionValues = parseExtractionValues(selectedDoc.extraction_values);
        if (extractionValues) {
          const categories = categorizeData(extractionValues, selectedDoc.auto_approval_data);
          Object.keys(categories).forEach((categoryName, idx) => {
            if (Object.keys(categories[categoryName]).length > 0) {
              newStates[`data-${idx}`] = false;
            }
          });
        }
      }
    }
    
    setOpenAccordions(prev => ({ ...prev, ...newStates }));
  };

  const toggleAllAccordions = (prefix) => {
    // Check if any accordion with this prefix is open
    const prefixAccordions = Object.keys(openAccordions).filter(key => key.startsWith(prefix));
    const anyOpen = prefixAccordions.some(key => openAccordions[key]);
    
    // If any are open, collapse all; otherwise expand all
    if (anyOpen) {
      collapseAllAccordions(prefix);
    } else {
      expandAllAccordions(prefix);
    }
  };

  const areAnyAccordionsOpen = (prefix) => {
    const prefixAccordions = Object.keys(openAccordions).filter(key => key.startsWith(prefix));
    return prefixAccordions.some(key => openAccordions[key]);
  };

  const categorizeData = (extractionValues, autoApprovalData) => {
    const categories = {
      'Insurance Data': {},
      'OneLoan Data': {}
    };

    // Parse auto_approval_data to get nested data structures
    // ONLY use data from auto_approval_data, ignore extraction_values
    if (autoApprovalData) {
      try {
        let parsedAutoApproval = autoApprovalData;
        if (typeof autoApprovalData === 'string') {
          parsedAutoApproval = JSON.parse(autoApprovalData);
        }
        
        // Ensure parsedAutoApproval is an object
        if (!parsedAutoApproval || typeof parsedAutoApproval !== 'object') {
          console.warn('auto_approval_data is not a valid object:', parsedAutoApproval);
          return categories;
        }
        
        const docExtractedData = parsedAutoApproval.doc_extracted_data || {};
        const oneLoanData = parsedAutoApproval['oneloan-data'] || {};
        
        console.log('Parsed auto_approval_data:', {
          docExtractedData: Object.keys(docExtractedData).length + ' fields',
          docExtractedDataKeys: Object.keys(docExtractedData),
          oneLoanData: Object.keys(oneLoanData).length + ' fields',
          oneLoanDataKeys: Object.keys(oneLoanData)
        });

        // 1. All fields from doc_extracted_data go to Insurance Data
        if (docExtractedData && typeof docExtractedData === 'object') {
          Object.entries(docExtractedData).forEach(([key, value]) => {
            // Skip metadata fields
            if (key === 'file_name' || key === 'loan_guid' || key === 'company' || 
                key === 'timestamp' || key === 'auto_approval_data' || key === 'document_id' ||
                key === 'doc_type' || key === 'created_at') {
              return;
            }
            categories['Insurance Data'][key] = value;
          });
        }

        // 2. All fields from oneloan-data go to OneLoan Data
        if (oneLoanData && typeof oneLoanData === 'object') {
          Object.entries(oneLoanData).forEach(([key, value]) => {
            // Skip metadata fields
            if (key === 'file_name' || key === 'loan_guid' || key === 'company' || 
                key === 'timestamp' || key === 'auto_approval_data' || key === 'document_id' ||
                key === 'doc_type' || key === 'created_at') {
              return;
            }
            categories['OneLoan Data'][key] = value;
          });
        }

      } catch (error) {
        console.error('Error parsing auto_approval_data:', error, autoApprovalData);
        // Return empty categories on error instead of crashing
      }
    }

    console.log('Categorized data:', {
      insurance: Object.keys(categories['Insurance Data']).length + ' fields',
      oneloan: Object.keys(categories['OneLoan Data']).length + ' fields'
    });

    return categories;
  };

  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/^extracted_/i, '')
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace('Hoi ', 'HOI ')
      .trim();
  };

  const formatValueForDisplay = (value) => {
    try {
      if (value === null || value === undefined) {
        return '--';
      }
      if (value === '') {
        return '--';
      }
      if (typeof value === 'object') {
        console.log('Converting object to string:', value);
        return JSON.stringify(value, null, 2);
      }
      return String(value);
    } catch (error) {
      console.error('Error formatting value:', error, value);
      return '[Error displaying value]';
    }
  };

  const getApprovalDecision = (doc) => {
    try {
      const autoApprovalResults = parseAutoApprovalResults(doc.auto_approval_results);
      if (autoApprovalResults && autoApprovalResults.final_decision) {
        return autoApprovalResults.final_decision;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const getApprovalClass = (decision) => {
    if (!decision) return 'approval-pending';
    const lowerDecision = decision.toLowerCase();
    
    // Check for negative decisions FIRST (before checking for "approved")
    if (lowerDecision.includes('not approved') || 
        lowerDecision.includes('reject') || 
        lowerDecision.includes('denied') ||
        lowerDecision.includes('disapproved') ||
        lowerDecision.includes('failed')) {
      return 'approval-rejected';
    }
    
    // Then check for positive decisions
    if (lowerDecision.includes('approved') || lowerDecision.includes('approve')) {
      return 'approval-approved';
    }
    
    return 'approval-pending';
  };

  const isApproved = (decision) => {
    if (!decision) return false;
    const lowerDecision = decision.toLowerCase();
    
    // Check for negative decisions FIRST
    if (lowerDecision.includes('not approved') || 
        lowerDecision.includes('reject') || 
        lowerDecision.includes('denied') ||
        lowerDecision.includes('disapproved') ||
        lowerDecision.includes('failed')) {
      return false;
    }
    
    // Then check for positive decisions
    return lowerDecision.includes('approved') || lowerDecision.includes('approve');
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredDocs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDocs = filteredDocs.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of table
    document.querySelector('.table-container')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Calculate enhanced stats with approval rate and today count
  const calculateEnhancedStats = () => {
    const totalDocs = documents.length;
    const approvedDocs = documents.filter(doc => {
      const decision = getApprovalDecision(doc);
      return decision && isApproved(decision);
    }).length;
    const rejectedDocs = documents.filter(doc => {
      const decision = getApprovalDecision(doc);
      return decision && !isApproved(decision) && decision.toLowerCase().includes('not');
    }).length;
    const pendingDocs = totalDocs - approvedDocs - rejectedDocs;
    const approvalRate = totalDocs > 0 ? Math.round((approvedDocs / totalDocs) * 100) : 0;

    // Calculate today's count
    const todayStart = startOfDay(new Date()).getTime();
    const todayCount = documents.filter(doc => {
      return (doc.initial_timestamp * 1000) >= todayStart;
    }).length;

    return {
      total: totalDocs,
      approved: approvedDocs,
      rejected: rejectedDocs,
      pending: pendingDocs,
      approvalRate,
      todayCount
    };
  };

  // Get timeline data for trend chart
  const getTimelineData = () => {
    const timeline = [];
    const daysToShow = trendPeriod;
    
    for (let i = daysToShow - 1; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dayStart = startOfDay(date).getTime();
      const dayEnd = endOfDay(date).getTime();
      
      const dayDocs = documents.filter(doc => {
        const timestamp = doc.initial_timestamp * 1000;
        return timestamp >= dayStart && timestamp <= dayEnd;
      });

      const approved = dayDocs.filter(doc => {
        const decision = getApprovalDecision(doc);
        return decision && isApproved(decision);
      }).length;

      const rejected = dayDocs.filter(doc => {
        const decision = getApprovalDecision(doc);
        return decision && !isApproved(decision) && decision.toLowerCase().includes('not');
      }).length;

      timeline.push({
        date: format(date, 'MM/dd'),
        approved,
        rejected,
        total: dayDocs.length
      });
    }
    return timeline;
  };

  const enhancedStats = calculateEnhancedStats();
  const timelineData = getTimelineData();

  return (
    <div className="monitoring-dashboard compact-layout">
      {/* Compact Header */}
      <div className="compact-header">
        <div className="header-left">
          <i className="fas fa-chart-line"></i>
          <h1>Insurance Monitor</h1>
          <div className="connection-badge">
            <span className={`status-dot ${isMonitoring ? 'connected' : 'disconnected'}`}></span>
            <span>{isMonitoring ? 'Live' : 'Stopped'}</span>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={startMonitoring} disabled={isMonitoring} className="btn-compact btn-start">
            <i className="fas fa-play"></i>
          </button>
          <button onClick={stopMonitoring} disabled={!isMonitoring} className="btn-compact btn-stop">
            <i className="fas fa-stop"></i>
          </button>
          <button onClick={handleRefresh} className="btn-compact btn-refresh">
            <i className="fas fa-sync-alt"></i>
          </button>
          <button onClick={() => setShowExportMenu(!showExportMenu)} className="btn-compact btn-export">
            <i className="fas fa-download"></i>
            {selectedDocs.size > 0 && <span className="badge-count">{selectedDocs.size}</span>}
          </button>
          {showExportMenu && (
            <div className="export-dropdown">
              <button onClick={() => { /* Add export function */ }}>
                <i className="fas fa-file-csv"></i> Export to CSV
              </button>
              <button onClick={() => { setSelectedDocs(new Set()); setShowExportMenu(false); }}>
                <i className="fas fa-times"></i> Clear Selection
              </button>
            </div>
          )}
          {selectedDocs.size > 0 && (
            <button
              className="btn-compact btn-delete-selected"
              onClick={handleDeleteSelected}
              disabled={isDeletingSelected}
              title={`Delete ${selectedDocs.size} selected record(s)`}
            >
              {isDeletingSelected
                ? <i className="fas fa-spinner fa-spin"></i>
                : <i className="fas fa-trash"></i>}
              <span>{selectedDocs.size}</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Content - Document Table */}
      <div className="monitor-content">
        {/* Top Row: Trend Chart and Stat Cards */}
        <div className="monitor-top-row">
          {/* Trend Chart */}
          <div className="chart-mini-card" style={{ gridColumn: '1 / 2' }}>
            <div className="chart-header-with-controls">
              <div className="chart-title">7-Day Trend</div>
              <div className="trend-period-selector">
                <button 
                  className={`period-btn ${trendPeriod === 7 ? 'active' : ''}`}
                  onClick={() => setTrendPeriod(7)}
                >
                  7D
                </button>
                <button 
                  className={`period-btn ${trendPeriod === 30 ? 'active' : ''}`}
                  onClick={() => setTrendPeriod(30)}
                >
                  30D
                </button>
                <button 
                  className={`period-btn ${trendPeriod === 90 ? 'active' : ''}`}
                  onClick={() => setTrendPeriod(90)}
                >
                  3M
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={80}>
              <AreaChart data={timelineData}>
                <defs>
                  <linearGradient id="colorApprovedMini" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="colorRejectedMini" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f7" />
                <XAxis dataKey="date" stroke="#d2d2d7" style={{ fontSize: '9px' }} />
                <YAxis stroke="#d2d2d7" style={{ fontSize: '9px' }} hide />
                <Tooltip contentStyle={{ background: 'white', border: 'none', borderRadius: '8px', fontSize: '10px', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }} />
                <Area type="monotone" dataKey="approved" stroke="#10b981" strokeWidth={1.5} fillOpacity={1} fill="url(#colorApprovedMini)" />
                <Area type="monotone" dataKey="rejected" stroke="#ef4444" strokeWidth={1.5} fillOpacity={1} fill="url(#colorRejectedMini)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Approval Rate Card */}
          <div className="chart-mini-card success">
            <div className="chart-title">Approval Rate</div>
            <div className="chart-value">{enhancedStats.approvalRate}%</div>
            <div className="chart-subtitle">Overall Success</div>
            <div className="mini-bar">
              <div className="mini-bar-fill" style={{ width: `${enhancedStats.approvalRate}%` }}></div>
            </div>
          </div>

          {/* Total Documents Card */}
          <div className="chart-mini-card">
            <div className="chart-title">Total Docs</div>
            <div className="chart-value">{enhancedStats.total}</div>
            <div className="chart-subtitle">Processed</div>
            <div className="mini-bar">
              <div className="mini-bar-fill" style={{ width: `${Math.min(100, (enhancedStats.total / 100) * 100)}%` }}></div>
            </div>
          </div>

          {/* Today's Documents Card */}
          <div className="chart-mini-card warning">
            <div className="chart-title">Today</div>
            <div className="chart-value">{enhancedStats.todayCount}</div>
            <div className="chart-subtitle">Documents</div>
            <div className="mini-bar">
              <div className="mini-bar-fill" style={{ width: `${Math.min(100, (enhancedStats.todayCount / enhancedStats.total) * 100)}%` }}></div>
            </div>
          </div>
        </div>

        {/* Second Row: Filters */}
        <div className="filters-row">
          <div className="filters-card-compact">
            <div className="filters-grid-compact">
              <div className="filter-group">
                <label><i className="fas fa-building"></i> Company</label>
                <select 
                  value={filters.company}
                  onChange={(e) => handleFilterChange('company', e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Companies</option>
                  <option value="gri">GRI</option>
                  <option value="gra">GRA</option>
                  <option value="op">OP</option>
                  <option value="kbhs">KBHS</option>
                </select>
              </div>

              <div className="filter-group">
                <label><i className="fas fa-filter"></i> Approval Status</label>
                <select 
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="not_approved">Not Approved</option>
                </select>
              </div>

              <div className="filter-group filter-search">
                <label><i className="fas fa-search"></i> Search</label>
                <input 
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  placeholder="Search by doc ID, loan GUID..."
                  className="filter-input"
                />
              </div>

              <div className="filter-group date-range-group">
                <label><i className="fas fa-calendar-alt"></i> Date Range</label>
                <div className="date-range-inputs">
                  <input 
                    type="date"
                    value={filters.dateFrom || ''}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className="date-input"
                    placeholder="From"
                  />
                  <span className="date-separator">to</span>
                  <input 
                    type="date"
                    value={filters.dateTo || ''}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className="date-input"
                    placeholder="To"
                  />
                </div>
              </div>

              <div className="filter-group filter-actions">
                <label>&nbsp;</label>
                <button onClick={() => setShowAdvancedSearch(true)} className="btn-advanced-search">
                  <i className="fas fa-search-plus"></i>
                  Advanced
                </button>
              </div>
            </div>

            <div className="results-count-compact">
              Showing <strong>{startIndex + 1}-{Math.min(endIndex, filteredDocs.length)}</strong> of <strong>{filteredDocs.length}</strong> documents
              {filteredDocs.length !== documents.length && ` (filtered from ${documents.length} total)`}
            </div>
          </div>
        </div>

        {/* Document Table - Main Feature */}
        <div className="data-table">
            <div className="table-header">
              <div className="header-content">
                <div className="header-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <div className="header-text">
                  <h4>Real-Time Workflow Monitoring</h4>
                  <p>Live DynamoDB data with instant updates</p>
                  <div className="live-indicator">
                    <span className="pulse-dot"></span>
                    <span>LIVE</span>
                    <span className="update-counter">Updates: {updateCount}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th className="checkbox-cell">
                      <input
                        type="checkbox"
                        checked={currentDocs.length > 0 && currentDocs.every(d => selectedDocs.has(d.doc_id))}
                        onChange={toggleSelectAll}
                        className="doc-checkbox"
                      />
                    </th>
                    <th>Document ID</th>
                    <th>Company</th>
                    <th>Status</th>
                    <th>Approval</th>
                    <th>Progress</th>
                    <th 
                      className="sortable-header"
                      onClick={() => handleSortChange('rules_passed')}
                      title="Click to sort by rules passed"
                    >
                      Rules Passed
                      {sortField === 'rules_passed' && (
                        <i className={`fas fa-sort-${sortOrder === 'desc' ? 'down' : 'up'} sort-icon`}></i>
                      )}
                    </th>
                    <th>Loan GUID</th>
                    <th 
                      className="sortable-header"
                      onClick={() => handleSortChange('timestamp')}
                      title="Click to sort by timestamp"
                    >
                      Timestamp
                      {sortField === 'timestamp' && (
                        <i className={`fas fa-sort-${sortOrder === 'desc' ? 'down' : 'up'} sort-icon`}></i>
                      )}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center">
                        <div className="loading-state">
                          <i className="fas fa-inbox"></i>
                          <p>{documents.length === 0 ? 'Loading data...' : 'No documents match your filters'}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentDocs.map((doc, index) => {
                      const approvalDecision = getApprovalDecision(doc);
                      const approvalClass = getApprovalClass(approvalDecision);
                      
                      return (
                        <tr key={index} className={`table-row-interactive ${selectedDocs.has(doc.doc_id) ? 'row-selected' : ''}`}>
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              checked={selectedDocs.has(doc.doc_id)}
                              onChange={() => toggleSelectDoc(doc.doc_id)}
                              className="doc-checkbox"
                            />
                          </td>
                          <td className="workflow-cell">
                            <span className="workflow-id">{doc.doc_id || '--'}</span>
                          </td>
                          <td className="company-cell">
                            <span className="company-badge">{doc.company || '--'}</span>
                          </td>
                          <td className="status-cell">
                            <span className={`status-badge ${getStatusBadgeClass(doc.extraction_status, doc.auto_approval_status)}`}>
                              {getStatusText(doc.extraction_status, doc.auto_approval_status)}
                            </span>
                          </td>
                          <td className="approval-cell">
                            {approvalDecision ? (
                              <span className={`approval-badge ${approvalClass}`}>
                                <i className={`fas ${approvalClass === 'approval-approved' ? 'fa-check-circle' : approvalClass === 'approval-rejected' ? 'fa-times-circle' : 'fa-clock'}`}></i>
                                {approvalDecision}
                              </span>
                            ) : (
                              <span className="approval-badge approval-pending">
                                <i className="fas fa-clock"></i>
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="progress-cell">
                            <div className="progress-bar-custom">
                              <div 
                                className="progress-fill" 
                                style={{ 
                                  width: `${getProgressPercentage(doc)}%`,
                                  background: getProgressPercentage(doc) === 100 
                                    ? '#16a34a'
                                    : '#2563eb'
                                }}
                              ></div>
                            </div>
                            <span className="progress-text">{getProgressPercentage(doc)}%</span>
                          </td>
                          <td className="rules-cell">
                            {getRulesDisplay(doc) !== '--' ? (
                              <span className={`rules-display ${getApprovalClass(approvalDecision)}`}>
                                {getRulesDisplay(doc)}
                              </span>
                            ) : (
                              <span className="rules-display-empty">--</span>
                            )}
                          </td>
                          <td className="data-cell">
                            <span>{doc.loan_guid || '--'}</span>
                          </td>
                          <td className="timestamp-cell">
                            <span className="timestamp">
                              {formatTimestamp(doc.initial_timestamp)}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <button
                              className="action-btn action-btn-details"
                              onClick={() => viewDetails(doc, 'both')}
                              disabled={!doc.extraction_values && !doc.auto_approval_results}
                              title="View Details"
                            >
                              <i className="fas fa-eye"></i>
                              <span>View Details</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                  Previous
                </button>
                
                <div className="pagination-info">
                  <span className="page-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 || 
                        page === totalPages || 
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            className={`page-number ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="page-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </span>
                  <span className="page-text">
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </span>
                </div>

                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </div>
        </div>

      {/* Detail Modal */}
      {selectedDoc && (
        <div className="modal-overlay" onClick={() => setSelectedDoc(null)}>
          <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fas fa-file-alt"></i> Document Details</h3>
              <button className="modal-close" onClick={() => setSelectedDoc(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {(() => {
                try {
                  const extractionValues = parseExtractionValues(selectedDoc.extraction_values);
                  const autoApprovalResults = parseAutoApprovalResults(selectedDoc.auto_approval_results);

                  return (
                  <div className="details-container">
                    {/* Document Info */}
                    <div className="detail-section">
                      <h4><i className="fas fa-info-circle"></i> Document Information</h4>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <span className="detail-label">Document ID:</span>
                          <span className="detail-value">{selectedDoc.doc_id}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Company:</span>
                          <span className="detail-value">{selectedDoc.company}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Loan GUID:</span>
                          <span className="detail-value">{selectedDoc.loan_guid}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">File Name:</span>
                          <span className="detail-value filename">{selectedDoc.file_name}</span>
                        </div>
                      </div>
                    </div>

                    {/* Side-by-side layout for AI Explanations and Data */}
                    <div className="side-by-side-container">
                      {/* Left side: Auto Approval Results with AI Explanations */}
                      {autoApprovalResults && (
                        <div className="detail-section side-section">
                          <div className="section-header">
                            <h4><i className="fas fa-brain"></i> AI Approval Analysis</h4>
                            <div className="section-controls">
                              <button 
                                className="btn-toggle"
                                onClick={() => toggleAllAccordions('criteria')}
                              >
                                <i className={`fas ${areAnyAccordionsOpen('criteria') ? 'fa-compress-alt' : 'fa-expand-alt'}`}></i>
                                {areAnyAccordionsOpen('criteria') ? 'Collapse All' : 'Expand All'}
                              </button>
                            </div>
                          </div>

                          <div className={`decision-banner-large ${
                            isApproved(autoApprovalResults.final_decision) 
                              ? 'approved' : 'rejected'
                          }`}>
                            <i className={`fas ${
                              isApproved(autoApprovalResults.final_decision) 
                                ? 'fa-check-circle' : 'fa-times-circle'
                            }`}></i>
                            <div>
                              <h5>{autoApprovalResults.final_decision}</h5>
                              <p>Passed {autoApprovalResults.num_pass} of {autoApprovalResults.query_count} criteria</p>
                            </div>
                          </div>

                          {autoApprovalResults.results && autoApprovalResults.results.length > 0 && (
                            <div className="criteria-accordion">
                              <h5 className="criteria-title">
                                <i className="fas fa-list-check"></i> Criteria & Explanations
                              </h5>
                              <div className="accordion" id="criteriaAccordion">
                                {autoApprovalResults.results.map((result, idx) => {
                                  const accordionId = `criteria-${idx}`;
                                  const isOpen = openAccordions[accordionId] || false;
                                  
                                  return (
                                    <div key={idx} className="accordion-item">
                                      <h2 className="accordion-header">
                                        <button 
                                          className={`accordion-button ${!isOpen ? 'collapsed' : ''}`}
                                          type="button"
                                          onClick={() => toggleAccordion(accordionId)}
                                        >
                                          <div className="accordion-title-content">
                                            <i className={`fas ${result.pass ? 'fa-check-circle' : 'fa-times-circle'} ${result.pass ? 'text-success' : 'text-danger'}`}></i>
                                            <span className="criteria-name">{result.query}</span>
                                            <span className={`badge ${result.pass ? 'bg-success' : 'bg-danger'}`}>
                                              {result.pass ? 'PASSED' : 'FAILED'}
                                            </span>
                                          </div>
                                        </button>
                                      </h2>
                                      <div className={`accordion-collapse ${isOpen ? 'show' : ''}`}>
                                        <div className="accordion-body">
                                          <div className="criteria-detail">
                                            <div className="criteria-rule-section">
                                              <h6 className="rule-label">
                                                <i className="fas fa-gavel"></i> Rule
                                              </h6>
                                              <div className="rule-name-display">
                                                {result.query}
                                              </div>
                                            </div>
                                            <div className="criteria-explanation-section">
                                              <h6 className="explanation-label">
                                                <i className="fas fa-robot"></i> AI Explanation
                                              </h6>
                                              <div className={`explanation-text ${result.pass ? 'pass' : 'fail'}`}>
                                                {result.explanation || 'No explanation available'}
                                              </div>
                                            </div>
                                            <div className="criteria-status-section">
                                              <div className={`criteria-status-indicator ${result.pass ? 'passed' : 'failed'}`}>
                                                <i className={`fas ${result.pass ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                                                <span>{result.pass ? 'Criteria Met' : 'Criteria Not Met'}</span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Right side: Extraction Values - Categorized */}
                      {extractionValues && (
                        <div className="detail-section side-section">
                          <div className="section-header">
                            <h4><i className="fas fa-database"></i> Extracted Document Data</h4>
                            <div className="section-controls">
                              <button 
                                className="btn-toggle"
                                onClick={() => toggleAllAccordions('data')}
                              >
                                <i className={`fas ${areAnyAccordionsOpen('data') ? 'fa-compress-alt' : 'fa-expand-alt'}`}></i>
                                {areAnyAccordionsOpen('data') ? 'Collapse All' : 'Expand All'}
                              </button>
                            </div>
                          </div>

                          <div className="accordion" id="dataAccordion">
                            {Object.entries(categorizeData(extractionValues, selectedDoc.auto_approval_data)).map(([categoryName, categoryData], catIdx) => {
                              const dataCount = Object.keys(categoryData).length;
                              
                              // Always show categories, even if empty, to indicate they exist
                              const accordionId = `data-${catIdx}`;
                              const isOpen = openAccordions[accordionId] || false;
                              const categoryIcon = categoryName === 'Insurance Data' ? 'fa-shield-alt' :
                                                  categoryName === 'OneLoan Data' ? 'fa-university' : 'fa-database';

                              return (
                                <div key={catIdx} className="accordion-item">
                                  <h2 className="accordion-header">
                                    <button 
                                      className={`accordion-button ${!isOpen ? 'collapsed' : ''}`}
                                      type="button"
                                      onClick={() => toggleAccordion(accordionId)}
                                      disabled={dataCount === 0}
                                    >
                                      <div className="accordion-title-content">
                                        <i className={`fas ${categoryIcon}`}></i>
                                        <span className="category-name">{categoryName}</span>
                                        <span className={`badge ${dataCount === 0 ? 'bg-secondary' : 'bg-info'}`}>
                                          {dataCount === 0 ? 'No data' : `${dataCount} fields`}
                                        </span>
                                      </div>
                                    </button>
                                  </h2>
                                  {dataCount > 0 && (
                                    <div className={`accordion-collapse ${isOpen ? 'show' : ''}`}>
                                      <div className="accordion-body">
                                        <div className="extraction-grid">
                                          {Object.entries(categoryData).map(([key, value]) => (
                                            <div key={key} className="extraction-item">
                                              <span className="extraction-label">
                                                {formatFieldName(key)}:
                                              </span>
                                              <span className="extraction-value">
                                                {formatValueForDisplay(value)}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Raw JSON - Optional toggle */}
                    <div className="detail-section">
                      <h4><i className="fas fa-code"></i> Raw Data (JSON)</h4>
                      <pre className="json-display">
                        {JSON.stringify(selectedDoc, null, 2)}
                      </pre>
                    </div>
                  </div>
                );
                } catch (error) {
                  console.error('Error rendering modal:', error);
                  return (
                    <div className="detail-section">
                      <h4 style={{ color: '#dc3545' }}><i className="fas fa-exclamation-triangle"></i> Error Loading Details</h4>
                      <p>There was an error loading the document details. Please check the browser console for more information.</p>
                      <pre style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', overflow: 'auto' }}>
                        {error.toString()}
                      </pre>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MonitoringDashboard;

